/**
 * Cleans and processes a URL parameter value by handling base64 encoding and JSON parsing
 * 
 * @param value - The URL parameter value to clean
 * @returns The processed value, either as a string or parsed JSON object
 */
export const cleanParam = (value: string): string | number | [] | object => {
    // First decode the URI component to handle URL encoding

    let processedValue: string;
    try {
        processedValue = decodeURIComponent(value);
    } catch (error) {
        // If decoding fails, return the original value
        return value;
    }

    // Check if the value is base64 encoded
    const base64Regex = /^base64:(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
    const isBase64 = base64Regex.test(value);

    if (isBase64) {
        try {
            processedValue = atob(value.replace(/^base64:/, ''));
        } catch (error) {
            // If base64 decoding fails, continue with the original decoded value
            console.warn('Failed to decode base64 value:', error);
        }
    }

    // Try to parse as JSON if the string starts with [ or {
    const jsonRegex = /^\[|{/;
    const mightBeJson = jsonRegex.test(processedValue);

    if (mightBeJson) {
        try {
            return JSON.parse(processedValue);
        } catch (error) {
            // If JSON parsing fails, return the processed string value
            console.warn('Failed to parse JSON value:', error);
        }
    }

    return processedValue;
};

/**
 * Parses URL parameters from both query string and hash fragments
 * 
 * @param url - The complete URL to parse
 * @returns An object containing all parsed parameters
 * @throws {Error} If the URL is invalid
 * 
 * @example
 * ```typescript
 * // Parse a URL with both query parameters and hash fragments
 * const params = parseUrlParameters('https://example.com?q=1&base64:SGVsbG8=#key=value');
 * ```
 */
export const parseUrlParameters = (url: string): { [key: string]: any } => {
    let urlObj: URL;

    try {
        urlObj = new URL(url);
    } catch (error) {
        throw new Error(`Invalid URL provided: ${(error as Error).message}`);
    }

    // Get parameters from both query string and hash
    const queryParams = new URLSearchParams(urlObj.search);
    const hashParams = new URLSearchParams(urlObj.hash.slice(1));

    // Combine and process all parameters
    const combinedParams: { [key: string]: any } = {};

    // Process query parameters
    queryParams.forEach((value, key) => {
        combinedParams[key] = cleanParam(value);
    });

    // Process hash parameters
    hashParams.forEach((value, key) => {
        combinedParams[key] = cleanParam(value);
    });

    return combinedParams;
};

export default parseUrlParameters;